<template>
  <div class="b-wrap" v-loading="loading">
    <div class="hero">
      <h2>确认您的归属代理商</h2>
      <p>
        代理商是您所在区域的服务商。平台按商家收益向代理支付分润，
        <b>不影响您的货款与利润</b>。
      </p>
    </div>

    <el-card shadow="never" class="mt-16px">
      <template #header>选择软件</template>
      <el-select v-model="softwareId" class="!w-320px" placeholder="请选择已接入分润的软件">
        <el-option
          v-for="s in softwareOptions"
          :key="s.softwareId"
          :label="s.softwareName || s.softwareId"
          :value="s.softwareId"
        />
      </el-select>
      <div v-if="!softwareOptions.length" class="hint">暂无已接入分润的软件，请联系平台开通。</div>
    </el-card>

    <el-card v-if="autoAssignEnabled" shadow="never" class="mt-16px">
      <template #header>
        <span>区域自动归属</span>
        <el-tag class="ml-8px" type="success" size="small">平台已开启</el-tag>
      </template>
      <el-form label-width="100px">
        <el-form-item label="所在区域">
          <el-input v-model="regionName" placeholder="如：杭州市-西湖区" class="!w-320px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="acting" :disabled="!softwareId" @click="doAuto">
            按区域自动匹配
          </el-button>
        </el-form-item>
      </el-form>
      <div class="hint">当前区域有代理则自动归属；没有代理则不做处理，可改为下方自行选择。</div>
    </el-card>

    <el-card v-else shadow="never" class="mt-16px">
      <el-alert type="info" :closable="false" title="平台未开启区域自动归属，请自行选择代理商或填写邀请码。" />
    </el-card>

    <el-card shadow="never" class="mt-16px">
      <template #header>自行选择代理商</template>
      <el-form label-width="100px">
        <el-form-item label="邀请码">
          <el-input
            v-model="inviteCode"
            placeholder="代理商编号（拓客链接会自动带入）"
            class="!w-320px"
            clearable
          />
          <el-button
            class="ml-8px"
            type="success"
            :loading="acting"
            :disabled="!softwareId || !inviteCode"
            @click="doInvite"
          >
            用邀请码确认
          </el-button>
        </el-form-item>
        <el-form-item label="筛选区域">
          <el-input
            v-model="filterRegion"
            placeholder="可按区域筛选，留空显示全部正常代理"
            class="!w-320px"
            clearable
            @change="loadAgents"
          />
          <el-button class="ml-8px" @click="loadAgents">刷新</el-button>
        </el-form-item>
        <el-form-item label="代理商" required>
          <el-select v-model="selectedAgentId" filterable class="!w-420px" placeholder="请选择代理商">
            <el-option
              v-for="a in agentOptions"
              :key="a.id"
              :label="`${a.name}（${a.agentNo || a.id}）${a.regionName ? ' · ' + a.regionName : ''}`"
              :value="a.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="acting" :disabled="!softwareId || !selectedAgentId" @click="doSelect">
            确认选择该代理
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div class="mt-16px">
      <el-button @click="$router.push('/store/belonging')">返回我的归属</el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  autoAssignBelonging,
  confirmBelonging,
  getBelongingAgents,
  getBelongingAutoAssignEnabled,
  getBelongingSoftware
} from '@/api/agent/belonging'
import { getShopInfo } from '@/api/merchant/shop'
import { getMerchantSession } from '@/views/merchant/registry/merchant-session'
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'

defineOptions({ name: 'MerchantBelongingConfirm' })

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const acting = ref(false)
const shopId = ref<number>()
const regionName = ref('')
const filterRegion = ref('')
const softwareId = ref('')
const selectedAgentId = ref<number>()
const inviteCode = ref(String(route.query.inviteCode || route.query.invite || '').trim())
const autoAssignEnabled = ref(false)
const softwareOptions = ref<{ softwareId: string; softwareName?: string }[]>([])
const agentOptions = ref<{ id: number; name?: string; agentNo?: string; regionName?: string }[]>([])

const resolveShop = async () => {
  const sess = getMerchantSession()
  const mall = sess?.modules?.find((m) => m.moduleId === 'MALL')
  if (mall?.subjectId) {
    const sid = Number(mall.subjectId)
    if (sid) shopId.value = sid
  }
  try {
    const shop = await getShopInfo(true)
    if (shop?.id) shopId.value = shop.id
  } catch {
    if (!shopId.value) {
      const sid = localStorage.getItem('merchantShopId')
      shopId.value = sid ? Number(sid) : undefined
    }
  }
}

const loadAgents = async () => {
  agentOptions.value =
    (await getBelongingAgents({
      regionName: filterRegion.value || undefined
    })) || []
}

onMounted(async () => {
  loading.value = true
  try {
    await resolveShop()
    autoAssignEnabled.value = !!(await getBelongingAutoAssignEnabled())
    softwareOptions.value = (await getBelongingSoftware()) || []
    if (softwareOptions.value.length) {
      softwareId.value = softwareOptions.value[0].softwareId
    }
    await loadAgents()
  } finally {
    loading.value = false
  }
})

const doAuto = async () => {
  if (!shopId.value || !softwareId.value) return ElMessage.warning('请先选择软件')
  if (!regionName.value) return ElMessage.warning('请填写所在区域')
  acting.value = true
  try {
    const ok = await autoAssignBelonging({
      merchantId: shopId.value,
      softwareId: softwareId.value,
      regionName: regionName.value
    })
    if (!ok) {
      ElMessage.warning('当前区域暂无可用代理，未做归属，请自行选择代理商')
      return
    }
    await confirmBelonging({
      merchantId: shopId.value,
      softwareId: softwareId.value,
      regionName: regionName.value
    })
    ElMessage.success('已按区域自动归属并确认')
    router.push('/store/belonging')
  } finally {
    acting.value = false
  }
}

const doSelect = async () => {
  if (!shopId.value || !softwareId.value || !selectedAgentId.value) {
    return ElMessage.warning('请选择软件和代理商')
  }
  acting.value = true
  try {
    await confirmBelonging({
      merchantId: shopId.value,
      softwareId: softwareId.value,
      agentId: selectedAgentId.value,
      regionName: filterRegion.value || regionName.value || undefined
    })
    ElMessage.success('已确认归属代理')
    router.push('/store/belonging')
  } finally {
    acting.value = false
  }
}

const doInvite = async () => {
  const code = inviteCode.value.trim()
  if (!shopId.value || !softwareId.value || !code) {
    return ElMessage.warning('请选择软件并填写邀请码')
  }
  acting.value = true
  try {
    await confirmBelonging({
      merchantId: shopId.value,
      softwareId: softwareId.value,
      inviteCode: code,
      regionName: filterRegion.value || regionName.value || undefined
    })
    ElMessage.success('已通过邀请码确认归属')
    router.push('/store/belonging')
  } finally {
    acting.value = false
  }
}
</script>

<style scoped>
.b-wrap { max-width: 860px; margin: 0 auto; }
.hero {
  background: linear-gradient(135deg, #102a4c, #1c3e6e);
  border-radius: 14px; color: #fff; padding: 26px 30px;
}
.hero h2 { font-size: 20px; margin: 0 0 6px; }
.hero p { margin: 0; opacity: .88; font-size: 13px; line-height: 1.6; }
.hint { margin-top: 8px; color: #909399; font-size: 12px; line-height: 1.5; }
</style>
